import { useState, useEffect, useRef, useCallback } from "react";

export const useCoverLetters = ({ authToken, coverTemplate, setCoverTemplate, setActiveTab }) => {
  const [coverLetters, setCoverLetters] = useState([]);
  const [activeCoverLetter, setActiveCoverLetter] = useState(null);
  const lastLoadedCoverLetterIdRef = useRef(null);

  // Sync cover letter templates with server state reactively
  useEffect(() => {
    if (!activeCoverLetter || !authToken) return;
    if (lastLoadedCoverLetterIdRef.current !== activeCoverLetter.id) return;
    if (activeCoverLetter.template === coverTemplate) return;

    const saveTemplate = async () => {
      try {
        const res = await fetch(`/api/cover-letter/${activeCoverLetter.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ template: coverTemplate }),
        });
        if (res.ok) {
          const updatedCl = await res.json();
          setCoverLetters((prev) =>
            prev.map((cl) =>
              cl.id === activeCoverLetter.id ? { ...cl, template: updatedCl.template } : cl
            )
          );
          setActiveCoverLetter((prev) =>
            prev ? { ...prev, template: updatedCl.template } : null
          );
        }
      } catch (err) {
        console.error(err);
      }
    };

    const t = setTimeout(saveTemplate, 300);
    return () => clearTimeout(t);
  }, [coverTemplate, activeCoverLetter?.id, authToken]);

  useEffect(() => {
    if (activeCoverLetter) {
      lastLoadedCoverLetterIdRef.current = activeCoverLetter.id;
      if (activeCoverLetter.template) {
        setCoverTemplate(activeCoverLetter.template);
      }
    } else {
      lastLoadedCoverLetterIdRef.current = null;
    }
  }, [activeCoverLetter?.id, setCoverTemplate]);

  const fetchCoverLetters = useCallback(async () => {
    if (!authToken) return;
    try {
      const res = await fetch("/api/cover-letter", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        const letters = data.cover_letters || [];
        setCoverLetters(letters);
      }
    } catch (err) {
      console.error(err);
    }
  }, [authToken]);

  useEffect(() => {
    if (authToken) {
      fetchCoverLetters();
    }
  }, [authToken, fetchCoverLetters]);

  const handleDeleteCoverLetter = useCallback(async (letterId) => {
    try {
      const res = await fetch(`/api/cover-letter/${letterId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        setCoverLetters((prev) => prev.filter((cl) => cl.id !== letterId));
        setActiveCoverLetter((prev) => {
          if (prev?.id === letterId) {
            const remaining = coverLetters.filter((cl) => cl.id !== letterId);
            return remaining.length > 0 ? remaining[0] : null;
          }
          return prev;
        });
      }
    } catch (err) {
      console.error(err);
    }
  }, [authToken, coverLetters]);

  const handleCreateCoverLetter = useCallback(async (switchDocumentFn) => {
    try {
      const res = await fetch("/api/cover-letter", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          company: "",
          job_title: "",
          template: "classic",
        }),
      });
      if (res.ok) {
        const newCl = await res.json();
        // [TRACE:cl-create] Stage 1 -- API response shape
        console.log(`[TRACE:cl-create] POST /api/cover-letter response:`, JSON.stringify({
          id: newCl.id,
          document_id: newCl.document_id,
          type: newCl.type,
          company: newCl.company,
          job_title: newCl.job_title,
          allKeys: Object.keys(newCl),
        }));
        setCoverLetters((prev) => [...prev, newCl]);
        setActiveCoverLetter(newCl);
        // CL-A fix: call switchDocument so the chat session also switches to this CL.
        // Canonical id is newCl.id (integer, legacy CoverLetter table has no document_id).
        if (switchDocumentFn) {
          console.log(`[TRACE:cl-create] calling switchDocument with id=${newCl.id}`);
          await switchDocumentFn({ type: 'cover_letter', id: newCl.id, document_data: newCl });
        } else {
          console.warn(`[TRACE:cl-create] switchDocument not passed -- chat session will NOT switch`);
          if (setActiveTab) setActiveTab('cover');
        }
      }
    } catch (err) {
      console.error("Error creating cover letter:", err);
    }
  }, [authToken, setActiveTab]);




  const updateCoverLetterState = useCallback((cl) => {
    if (!cl) {
      setActiveCoverLetter(null);
      return;
    }
    setActiveCoverLetter((prev) => {
      if (prev && String(prev.id) !== String(cl.id)) {
        // Different cover letter, do not merge with old letter state
        return cl;
      }
      const prevCompany = prev?.company || "";
      const prevJobTitle = prev?.job_title || "";
      const prevRecipient = prev?.recipient || "";
      const prevSender = prev?.sender || {};
      const prevCreatedAt = prev?.created_at || "";
      const prevContent = prev?.content || "";
      const prevTemplate = prev?.template || "";

      const updated = {
        ...prev,
        ...cl,
        id: cl.id ?? prev?.id,
        company: cl.company || prevCompany,
        job_title: cl.job_title || prevJobTitle,
        recipient: cl.recipient || prevRecipient,
        sender: (cl.sender && Object.keys(cl.sender).length) ? cl.sender : prevSender,
        created_at: cl.created_at || prevCreatedAt,
        content: cl.content || prevContent,
        template: cl.template || prevTemplate,
      };
      console.log("[TRACE:CL_SYNC] Merged activeCoverLetter state:", updated);
      return updated;
    });

    setCoverLetters((prevList) => {
      return prevList.map((item) => {
        if (String(item.id) === String(cl.id)) {
          const itemCompany = item.company || "";
          const itemJobTitle = item.job_title || "";
          const itemRecipient = item.recipient || "";
          const itemCreatedAt = item.created_at || "";
          const itemContent = item.content || "";
          const itemTemplate = item.template || "";

          const mergedItem = {
            ...item,
            ...cl,
            id: cl.id,
            company: cl.company || itemCompany,
            job_title: cl.job_title || itemJobTitle,
            recipient: cl.recipient || itemRecipient,
            created_at: cl.created_at || itemCreatedAt,
            content: cl.content || itemContent,
            template: cl.template || itemTemplate,
          };
          console.log("[TRACE:CL_SYNC] Merged coverLetters[] item state:", mergedItem);
          return mergedItem;
        }
        return item;
      });
    });
  }, []);

  return {
    coverLetters,
    setCoverLetters,
    activeCoverLetter,
    setActiveCoverLetter,
    updateCoverLetterState,
    fetchCoverLetters,
    handleDeleteCoverLetter,
    handleCreateCoverLetter,
  };
};
